import type { ContentObjectOption, ContentObjectProps } from '@openx/types/targeting/contentObject';
import type { TargetingReadOnlyProps } from '@openx/types/targeting/targeting';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingDisabledState } from '../../utils/hooks/useTargetingDisabledState';
import { useDropdownOpen } from '../shared/ReadOnlyTargetingDropdown/hooks/useDropdownOpen';
import { StyledReadOnlyTargetingDropdown } from '../shared/ReadOnlyTargetingDropdown/ReadOnlyTargetingDropdown';
import { TargetingReadOnly } from '../shared/TargetingReadOnly/TargetingReadOnly';
import { ContentObjectFormPreviewItems } from './ContentObjectItems/ContentObjectFormPreviewItems';
import { ContentObjectItems } from './ContentObjectItems/ContentObjectItems';
import { filterEmptyParams, getDefaultParams } from './utils/utils';

export const ContentObjectReadOnly = ({
  name,
  onOpenDrawerClick,
  onRemoveClick,
  targetingParams,
  readonly,
  field,
  onRemoveParameter,
}: TargetingReadOnlyProps<ContentObjectProps> & {
  onRemoveParameter?: (opt: ContentObjectOption) => void;
  field: string;
}) => {
  const { isDisabledOrHasError, disabledReasonMessage, errorReasonMessage } = useTargetingDisabledState(field);

  const { t } = useTranslation();
  const paramsKeys = targetingParams && Object.keys(filterEmptyParams(targetingParams));
  const optionsCount = paramsKeys?.length || undefined;
  const hasValue = optionsCount !== undefined && optionsCount > 0;
  const { isOpen, toggleOpen } = useDropdownOpen(hasValue);
  const isEmpty = useMemo(() => !paramsKeys?.length, [paramsKeys]);

  if (!readonly)
    return (
      <TargetingReadOnly
        name={name}
        field={field}
        isEmpty={isEmpty}
        onOpenDrawerClick={onOpenDrawerClick}
        onRemove={onRemoveClick}
        readonly={readonly}
        isDisabled={isDisabledOrHasError}
        disabledReasonMessage={disabledReasonMessage}
        description={t(
          'Target specific content characteristics like genre, channel, and network to better match campaign messaging.'
        )}
        optionsCount={optionsCount}
        isOpen={isOpen}
        toggleOpen={toggleOpen}
      >
        {isOpen && (
          <StyledReadOnlyTargetingDropdown>
            <ContentObjectFormPreviewItems
              hideButtons={false}
              targetingParams={targetingParams ?? getDefaultParams()}
              readonly
              onOpenDrawerClick={onOpenDrawerClick}
              onRemove={onRemoveClick}
              onRemoveParameter={onRemoveParameter}
              optionsCount={optionsCount}
              disabledReasonMessage={errorReasonMessage}
            />
          </StyledReadOnlyTargetingDropdown>
        )}
      </TargetingReadOnly>
    );

  return (
    <TargetingReadOnly
      name={name}
      field={field}
      isEmpty={isEmpty}
      onOpenDrawerClick={onOpenDrawerClick}
      onRemove={onRemoveClick}
      readonly={readonly}
      isDisabled={isDisabledOrHasError}
      disabledReasonMessage={disabledReasonMessage}
      description={t(
        'Target specific content characteristics like genre, channel, and network to better match campaign messaging.'
      )}
    >
      {!isEmpty && targetingParams && <ContentObjectItems targetingParams={targetingParams} readonly />}
    </TargetingReadOnly>
  );
};
